import React, { useState, ReactElement } from 'react';
import {
  useCreateCardTemplateMutation,
  CardTemplatesQuery,
} from '@trello/graphql/generated';
import { Button } from '@trello/nachos';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Icon, IconName } from 'app/common/components/controls';
import { useShortcut, Scope, Key, getKey } from '@trello/keybindings';
import { forTemplate } from 'app/common/lib/util/i18n';
import cx from 'classnames';

import query from './CardTemplatesQuery.graphql';

import styles from './TemplateCardComposer.less';

const format = forTemplate('card-templates');
const composerFormat = forTemplate('card_composer_inline');

interface TemplateCardComposerProps {
  idList: string;
  idBoard: string;
  closedComponent?: (onClick: () => void) => ReactElement;
  onCreate?: (card: { id: string; shortLink: string }) => void;
}

export const TemplateCardComposer: React.FunctionComponent<TemplateCardComposerProps> = ({
  idList,
  idBoard,
  closedComponent,
  onCreate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');

  const [
    createNewTemplate,
    { loading: creating },
  ] = useCreateCardTemplateMutation();

  useShortcut(
    () => {
      setIsOpen(false);
    },
    {
      scope: Scope.Popover,
      key: Key.Escape,
      enabled: isOpen,
    },
  );

  const create = () => {
    return createNewTemplate({
      variables: {
        listId: idList,
        name: name.trim(),
        closed: false,
      },
      update(cache, { data }) {
        const newCard = data && data.createCardTemplate;
        if (!newCard) {
          return;
        }

        const current: CardTemplatesQuery | null = cache.readQuery({
          query: query,
          variables: { boardId: idBoard },
        });
        if (!current) {
          return;
        }

        const board = current.board;
        const cards = board && board.cards;

        if (!cards) {
          return;
        }

        cache.writeQuery({
          query: query,
          variables: {
            boardId: idBoard,
          },
          data: {
            board: {
              ...board,
              cards: cards.concat(newCard),
            },
          },
        });
      },
    });
  };

  const clear = () => {
    setName('');
    setIsOpen(false);
  };

  const onSubmit = async () => {
    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      return;
    }

    const { data } = await create();

    trackUe({
      category: Noun.CARD_TEMPLATES,
      verb: Verb.CREATES,
      directObj: Noun.CARD_TEMPLATE,
      indirectObj: Noun.CARD_TEMPLATES_MENU,
      method: 'by clicking the create new template button',
    });

    clear();

    if (onCreate && data && data.createCardTemplate) {
      const card = data.createCardTemplate;
      onCreate(card);
    }
  };

  if (!isOpen) {
    if (!closedComponent) {
      return (
        <a
          role="button"
          className={styles.openTemplateCardComposer}
          onClick={() => setIsOpen(true)}
        >
          <Icon name={IconName.Add} className={styles.iconAdd} small />
          <span>{format('create-a-new-template')}</span>
        </a>
      );
    }

    return closedComponent(() => setIsOpen(true));
  }

  return (
    <>
      <div className={styles.listCard}>
        <div className={cx(styles.listCardDetails, 'u-clearfix')}>
          <textarea
            value={name}
            className={styles.templateCardComposerTextarea}
            dir="auto"
            placeholder={format('template-title')}
            autoFocus={true}
            onBlur={clear}
            onKeyDown={(e) => {
              if (getKey(e) === Key.Enter) {
                e.preventDefault();
                onSubmit();
              }
            }}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      </div>
      <div>
        <Button
          onClick={onSubmit}
          className={styles.addButton}
          appearance="primary"
          isDisabled={creating}
          isLoading={creating}
        >
          {composerFormat('add')}
        </Button>
        <a role="button" className={styles.closeButton} onClick={clear}>
          <Icon className={styles.closeIcon} name={IconName.Close} large />
        </a>
      </div>
    </>
  );
};
